import React, { useEffect, useMemo } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { Box, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import LoadingScreen from '../../../LoadingScreen';
import Column from '../../../Column';
import { ScoreClusterLabels } from '../../../../dto/Types/ScoreClusterLabelType';

type Point = { x: number; y: number };
type ColorCluster = 'bad' | 'poor' | 'moderate' | 'good' | 'high' | 'empty';
type ScoreClusterWaffleChartData = Record<ColorCluster, Point[]>;

export interface ScoreClusterWaffleChartProps {
  type: 'scoreClusterWaffleChart';
  data: ScoreClusterWaffleChartData;
  label: ScoreClusterLabels;
  percentage: string;
  absValue: number | '0';
}

const ScoreClusterWaffleChart: React.FC<ScoreClusterWaffleChartProps> = ({ data, label, percentage, absValue }) => {
  const [completedData, setCompletedData] = React.useState<ScoreClusterWaffleChartData>(data);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const waffleId = useMemo(() => generateUniqueId(), []);

  useEffect(() => {
    // Fill the empty grid with the missing points
    const newData = fillEmptyGrid({ ...data });
    setCompletedData(newData);
  }, [data]);

  useEffect(() => {
    am4core.addLicense('CH443808305');
  }, []);
  useEffect(() => {
    // Create chart instance
    const chart = am4core.create(`waffle-chart-container${waffleId}`, am4charts.XYChart);

    // Create series
    const xAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    xAxis.dataFields.category = 'x';
    xAxis.renderer.grid.template.location = 0;
    xAxis.renderer.minGridDistance = 10;
    xAxis.renderer.labels.template.disabled = true;
    xAxis.data = [...Array(10)].map((_, i) => ({ x: String(i + 1) }));

    const yAxis = chart.yAxes.push(new am4charts.CategoryAxis());
    yAxis.dataFields.category = 'y';
    yAxis.renderer.grid.template.location = 0;
    yAxis.renderer.minGridDistance = 10;
    yAxis.renderer.labels.template.disabled = true;
    yAxis.data = [...Array(10)].map((_, i) => ({ y: String(i + 1) }));

    if (completedData) {
      Object.keys(completedData).forEach((d) => {
        const series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.categoryX = 'x';
        series.dataFields.categoryY = 'y';
        series.sequencedInterpolation = true;
        series.defaultState.transitionDuration = 3000;
        series.name = d.charAt(0).toUpperCase() + d.slice(1);

        // Add waffle chart template
        const column = series.columns.template;
        column.strokeWidth = 3;
        column.strokeOpacity = 1;
        column.stroke = am4core.color('#fff');
        column.width = am4core.percent(100);
        column.height = am4core.percent(100);

        // Set data
        series.data = completedData[d as ColorCluster];
        series.fill = new am4core.Color(getColorClusterColorWithEmpty(d));
      });
      setIsLoading(false);
    }
    return () => {
      chart.dispose(); // Cleanup when the component is unmounted
    };
  }, [completedData]);

  return (
    <>
      {isLoading && <LoadingScreen variant="circle" />}
      {!isLoading && (
        <WaffleColumn key={`waffle-chart-${waffleId}`}>
          <Typography variant="body2">{label}</Typography>
          <Box
            component="div"
            id={`waffle-chart-container${waffleId}`}
            style={{
              width: '100%',
              height: isTablet ? (isMobile ? '70vw' : '50vw') : '15vw',
              maxHeight: '300px',
            }}
          />
          <Typography variant="body2">{percentage}</Typography>
          <Typography variant="body2" style={{ fontWeight: 400 }}>
            {absValue}
          </Typography>
        </WaffleColumn>
      )}
    </>
  );
};

export default ScoreClusterWaffleChart;

export type GridObject = {
  bad: Point[];
  poor: Point[];
  moderate: Point[];
  good: Point[];
  high: Point[];
  empty: Point[];
};

export const fillEmptyGrid = (input: GridObject): GridObject => {
  const gridSize = 10;

  // Count the total number of existing points
  const existingPoints = [...input.bad, ...input.poor, ...input.moderate, ...input.good, ...input.high, ...input.empty];

  // Create a set of all possible points in a 10x10 grid
  const allPoints: Point[] = [];
  for (let x = 1; x <= gridSize; x++) {
    for (let y = 1; y <= gridSize; y++) {
      allPoints.push({ x, y });
    }
  }

  // Find the points that are not yet used
  const usedPoints = new Set(existingPoints.map((p) => `${p.x},${p.y}`));
  const missingPoints = allPoints.filter((p) => !usedPoints.has(`${p.x},${p.y}`));

  // Fill the "empty" array with the missing points
  input.empty = missingPoints;

  return input;
};

export const getColorClusterColorWithEmpty = (cluster: string) => {
  switch (cluster) {
    case 'poor':
      return { r: 209, g: 67, b: 67, a: 0.8 };
    case 'bad':
      return { r: 209, g: 67, b: 67, a: 0.4 };
    case 'moderate':
      return { r: 255, g: 176, b: 32, a: 0.8 };
    case 'good':
      return { r: 16, g: 185, b: 129, a: 0.4 };
    case 'high':
      return { r: 16, g: 185, b: 129, a: 0.8 };
    case 'empty':
      return { r: 222, g: 226, b: 233, a: 1 };
  }
};

export const generateUniqueId = () => {
  return `${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
};

export const generatePoints = (n: number): Point[] => {
  const result: Point[] = [];

  for (let i = 0; i < n; i++) {
    const x = (i % 10) + 1; // x goes from 1 to 10
    const y = Math.floor(i / 10) + 1; // y grows by 1 every 10 elements
    result.push({ x, y });
  }

  return result;
};

const WaffleColumn = styled(Column)(() => ({
  marginBottom: 24,
  '& p': {
    fontWeight: 'bold',
    textTransform: 'capitalize',
    textAlign: 'center',
    '&:last-of-type': {
      fontWeight: 400,
    },
  },
  '&:last-of-type': {
    marginBottom: 0,
  },
}));
